import React from "react";
import { Table, Tag, Space, Button, Popconfirm, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const INVOLVEMENT_COLORS = {
  Active: "green",
  Occasional: "blue",
  "Non-participant": "default",
};

const RATING_COLORS = {
  Excellent: "green",
  Good: "blue",
  Average: "orange",
  "Needs Improvement": "red",
};

const ActivityParticipationTable = ({
  participations = [],
  loading,
  isAdmin,
  onEdit,
  onDelete,
}) => {
  const columns = [
    {
      title: "Student",
      dataIndex: "studentName",
      key: "studentName",
      sorter: (a, b) =>
        (a.studentName || "").localeCompare(b.studentName || ""),
    },
    {
      title: "Event",
      dataIndex: "eventTitle",
      key: "eventTitle",
      sorter: (a, b) => (a.eventTitle || "").localeCompare(b.eventTitle || ""),
    },
    {
      title: "Date",
      dataIndex: "eventDate",
      key: "eventDate",
      sorter: (a, b) => new Date(a.eventDate) - new Date(b.eventDate),
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Category",
      dataIndex: "eventCategory",
      key: "eventCategory",
      align: "center",
      render: (v) => <Tag>{v || "-"}</Tag>,
    },
    {
      title: "Involvement",
      dataIndex: "involvementLevel",
      key: "involvementLevel",
      align: "center",
      render: (v) => <Tag color={INVOLVEMENT_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Rating",
      dataIndex: "performanceRating",
      key: "performanceRating",
      align: "center",
      render: (v) =>
        v ? <Tag color={RATING_COLORS[v] || "default"}>{v}</Tag> : "-",
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      align: "center",
      render: (v) => v ?? "-",
      sorter: (a, b) => (a.score ?? -1) - (b.score ?? -1),
    },
    {
      title: "Award",
      key: "award",
      align: "center",
      render: (_, record) =>
        record.hasAward ? (
          <Tooltip title={record.awardTitle || "Award"}>
            <TrophyOutlined style={{ color: "#faad14", fontSize: 16 }} />
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Recorded By",
      dataIndex: "recordedBy",
      key: "recordedBy",
      render: (v) => v || "-",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          {isAdmin && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this participation record?"
                description="This cannot be undone."
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={participations}
      rowKey="id"
      loading={loading}
      size="small"
      scroll={{ x: 1100 }}
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} records`,
      }}
    />
  );
};

export default ActivityParticipationTable;
